import React, { useEffect, useState } from 'react';
import { all_routes } from '../../router/all_routes';
import { Link } from 'react-router-dom';
import Table from '../../../core/common/dataTable/index';
import CollapseHeader from '../../../core/common/collapse-header/collapse-header';
import useFetch from '../../../hooks/useFetch';
import { API } from '../../../common/Constants';
import { getAPIHeaders } from '../../../common/Utils';
import OnboardEmployee from './onboardEmployee';
import ModifyEmployee from './modifyEmployee';
import PreLoader from '../../../core/common/preLoader';
import { Employee } from '../../../types/dataModel';
import CopyRightInfo from '../../../core/common/copyRightInfo';
import { useAccess } from '../../../hooks/accessContext';
import DeleteModal from '../../../core/modals/deleteModal';
import CustomToast from '../../../core/common/customToast';
import FacultyScheduleModal from './facultySchedule';

const EmployeeList = () => {
  const { hasOrgMgntCreate, hasOrgMgntEdit, hasOrgMgntDelete } = useAccess();
  const { isLoading, callFetch } = useFetch();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [designationOpts, setDesignationOpts] = useState<any[]>([]);
  const [editEmployee, setEditEmployee] = useState<Employee | null>(null);
  const [roleOpts, setRoleOpts] = useState<any[]>([]);
  const [editEmpRoles, setEditEmpRoles] = useState<any[]>([]);
  const [stats, setStats] = useState<any>();
  const [viewType, setViewType] = useState<'table' | 'card'>('table');
  const [deleteItem, setDeleteItem] = useState<any>({
    id: undefined,
    message: undefined,
  });
  const [toastSettings, setToastSettings] = useState<{
    show: boolean;
    message: string;
    heading: string;
    type: 'ERROR' | 'SUCCESS' | 'INFO';
  }>({
    show: false,
    message: 'Something went wrong please try again later',
    heading: 'Oops! Something went wrong',
    type: 'ERROR',
  });
  const [showFacultySchedule, setShowFacultySchedule] = useState<boolean>(false);

  useEffect(() => {
    fetchEmployees();
    fetchDesignation();
    fetchRoles();
  }, []);

  const fetchEmployees = () => {
    callFetch(API.EMPLOYEE, 'GET', getAPIHeaders(), null, null).then((res) => {
      setEmployees(res?.employeeProfiles);
      setStats(res?.employeeStatsCard);
    });
  };

  const fetchDesignation = () => {
    callFetch(API.DESIGNATION, 'GET', getAPIHeaders(), null, null).then(
      (res: any[]) => {
        if (res) {
          let opts: any[] = [];
          res.forEach((item) => {
            opts.push({
              label: item.name,
              value: item.id,
            });
          });
          setDesignationOpts(opts);
        }
      }
    );
  };

  const fetchRoles = () => {
    callFetch(
      `${API.ROLES}?includePermissions=false`,
      'GET',
      getAPIHeaders(),
      null,
      null
    ).then((res: any[]) => {
      if (res) {
        let opts: any[] = [];
        res.forEach((item) => {
          opts.push({
            label: item.name,
            value: item.id,
          });
        });
        setRoleOpts(opts);
      }
    });
  };

  const fetchEmployeeRoles = (id: number) => {
    setEditEmpRoles([]);
    callFetch(`${API.USER_ACCT}/${id}/roles`, 'GET', getAPIHeaders(), null, null).then(
      (res: any[]) => {
        if (res) {
          let opts: any[] = [];
          res.forEach((item) => {
            opts.push({
              label: item.name,
              value: item.id,
            });
          });
          setEditEmpRoles(opts);
        }
      }
    );
  };

  const deleteData = (id: number) => {
    callFetch(`${API.EMPLOYEE}/${id}`, 'DELETE', getAPIHeaders(), null, null)
      .then(() => {
        fetchEmployees();
      })
      .catch((error) => {
        setToastSettings((prev) => ({
          ...prev,
          show: true,
          message: error?.response?.data || 'Something went wrong please try again later',
        }));
      });
  };

  const columns = [
    {
      title: 'Emp ID',
      dataIndex: 'employeeId',
      render: (text: String, record: any) => (
        <span className='text-dark'>{text}</span>
      ),
      sorter: (a: any, b: any) => a.employeeId - b.employeeId,
    },
    {
      title: 'Name',
      dataIndex: 'firstName',
      render: (text: String, record: Employee) => (
        <div className="d-flex align-items-center">
          {record.profilePictureUrl ? (
            <div className="avatar avatar-md me-2">
              <img 
                src={record.profilePictureUrl} 
                alt={`${record.firstName || ''} ${record.lastName || ''}`}
                className="avatar-img rounded-circle"
              />
            </div>
          ) : (
            <div className="avatar avatar-md me-2 bg-light-primary rounded-circle">
              <span className="text-primary fw-bold">
                {(record.firstName || '').charAt(0).toUpperCase()}{(record.lastName || '').charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          <div className="ms-2 employee-title">
            <p className="text-dark mb-0">
              <h6 className="fw-medium">
                <Link 
                  to="#" 
                  className="text-reset text-decoration-none"
                  onClick={() => { onEdit(record) }}
                  data-bs-toggle="modal"
                  data-bs-target="#edit_employee"
                >
                  {record.firstName || ''} {record.lastName || ''}
                </Link>
              </h6>
            </p>
            <div className="d-flex align-items-left mt-1">
              <span className="fs-12 text-muted">{record.designationName || ''}</span>
            </div>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) =>
        (a?.firstName || '').concat(a?.lastName || '').localeCompare((b?.firstName || '').concat(b?.lastName || '')),
      defaultSortOrder: 'ascend',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      render: (text: String, record: Employee) => (
        <div className="d-flex align-items-center">
          <span className="text-wrap">
            <i className="ti ti-mail me-1 text-primary"></i> {record.email || ''}
          </span>
        </div>
      ),
      sorter: (a: any, b: any) => (a?.email || '').localeCompare(b?.email || ''),
    },
    {
      title: 'Phone',
      dataIndex: 'phoneNumber',
      render: (text: String, record: Employee) => (
        <div className="d-flex align-items-center">
          <span>
            <i className="ti ti-phone me-1 text-secondary"></i> {record.phoneNumber || ''}
          </span>
        </div>
      ),
      sorter: (a: any, b: any) => (a?.phoneNumber || '').localeCompare(b?.phoneNumber || ''),
    },
    {
      title: 'Designation',
      dataIndex: 'designationName',
      render: (text: String, record: Employee) => (
        <div className="d-flex align-items-center">
          <div className="ms-2">
            <p className="text-dark mb-0">
              <h6 className="fw-medium">
                <Link 
                  to="#" 
                  className="text-reset text-decoration-none"
                  onClick={() => { onEdit(record) }}
                  data-bs-toggle="modal"
                  data-bs-target="#edit_employee"
                >
                  {record.designationName || ''}
                </Link>
              </h6>
            </p>
            <div className="d-flex align-items-center mt-1 org-info">
              <span className="badge badge-soft-info me-2 fs-12">
                {record.departmentName || ''}
              </span>
            </div>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) => (a?.designationName || '').localeCompare(b?.designationName || ''),
    },
    {
      title: 'Username',
      dataIndex: 'username',
      render: (text: String, record: Employee) => (
        <div className="d-flex align-items-center">
          <div className="ms-2">
            <p className="text-dark mb-0">
              <h6 className="fw-medium">
                <Link 
                  to="#" 
                  className="text-reset text-decoration-none"
                  onClick={() => { onEdit(record) }}
                  data-bs-toggle="modal"
                  data-bs-target="#edit_employee"
                >
                  {record.username}
                </Link>
              </h6>
            </p>
            <div className="d-flex align-items-center mt-1 account-info">
              <span className="badge badge-soft-success me-2 fs-12">
                Acct# {record.accountId}
              </span>
            </div>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) => a.username?.localeCompare(b.username),
    },
    {
      title: 'Actions',
      dataIndex: '',
      render: (text: String, record: any) => (
        <div className="action-icon d-inline-flex">
          <Link
            to="#"
            hidden={!record.roles.includes('FACULTY')}
            className="me-2 icon-calendar"
            onClick={() => {
              setEditEmployee(record);
              setShowFacultySchedule(true);
            }}
            title="View Faculty Schedule"
          >
            <i className="ti ti-calendar" />
          </Link>
          <Link
            hidden={!hasOrgMgntEdit()}
            to="#"
            className="me-2 icon-edit"
            data-bs-toggle="modal"
            data-inert={true}
            data-bs-target="#edit_employee"
            onClick={() => {
              onEdit(record);
            }}
            title="Edit Employee"
          >
            <i className="ti ti-edit" />
          </Link>
          <Link
            hidden={!hasOrgMgntDelete()}
            to="#"
            className="icon-delete"
            data-bs-toggle="modal"
            data-inert={true}
            data-bs-target="#delete_modal"
            onClick={() =>
              setDeleteItem({
                id: record.employeeId,
                message: `Are you sure you want to delete ${record.firstName} ${record.lastName}? This can't be undone.`,
              })
            }
            title="Delete Employee"
          >
            <i className="ti ti-trash" />
          </Link>
        </div>
      ),
    },
  ];

  const onEdit = (employee: Employee) => {
    if (employee) {
      setEditEmployee(employee);
    }

    if (employee.accountId) {
      fetchEmployeeRoles(employee.accountId);
    }
  };

  const renderAddEmployee = () => {
    return (
      <OnboardEmployee
        roleOpts={roleOpts}
        designations={designationOpts}
        onSuccess={fetchEmployees}
      />
    );
  };

  const renderEditEmployee = () => {
    return (
      <ModifyEmployee
        roleOpts={roleOpts}
        designations={designationOpts}
        employee={employees?.find((el: Employee) => el.employeeId === editEmployee?.employeeId)}
        onSuccess={fetchEmployees}
        employeeRoles={editEmpRoles}
      />
    );
  };

  const renderFacultySchedule = () => {
    return (
      <FacultyScheduleModal
        show={showFacultySchedule}
        employee={editEmployee || undefined}
        onClose={() => setShowFacultySchedule(false)}
      />
    );
  };

  const renderStatsCards = () => {
    return (
      <div className="row">
        <div className="col-xl-3 col-md-6">
          <div className="card">
            <div className="card-body">
              <div className="d-flex align-items-center flex-wrap justify-content-between">
                <div>
                  <p className="fs-12 fw-medium mb-0 text-gray-5">Total Active Employees</p>

                  <span className="flex-row d-flex">
                    <h4>{stats?.totalEmployees}</h4>
                    <span className="ms-1 fs-12 align-self-center">
                      (Male: <span className="text-dark me-2">{stats?.maleStaff}</span>
                      Female: <span className="text-dark">{stats?.femaleStaff}</span>)
                    </span>
                  </span>
                </div>
                <div>
                  <span className="p-2 br-10 bg-transparent-primary border border-primary d-flex align-items-center justify-content-center">
                    <i className="ti ti-user text-primary fs-18"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-xl-3 col-md-6">
          <div className="card">
            <div className="card-body">
              <div className="d-flex align-items-center flex-wrap justify-content-between">
                <div>
                  <p className="fs-12 fw-medium mb-0 text-gray-5">With Admin Access</p>
                  <h4>{stats?.administrativeStaff}</h4>
                </div>
                <div>
                  <span className="p-2 br-10 bg-pink-transparent border border-pink d-flex align-items-center justify-content-center">
                    <i className="ti ti-user text-pink fs-18"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-xl-3 col-md-6">
          <div className="card">
            <div className="card-body">
              <div className="d-flex align-items-center flex-wrap justify-content-between">
                <div>
                  <p className="fs-12 fw-medium mb-0 text-gray-5">Total Faculties</p>
                  <h4>{stats?.teachingStaff}</h4>
                </div>
                <div>
                  <span className="p-2 br-10 bg-transparent-purple border border-purple d-flex align-items-center justify-content-center">
                    <i className="ti ti-user text-purple fs-18"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-xl-3 col-md-6">
          <div className="card">
            <div className="card-body">
              <div className="d-flex align-items-center flex-wrap justify-content-between">
                <div>
                  <p className="fs-12 fw-medium mb-0 text-gray-5">Inactive Employees</p>
                  <h4>{stats?.inactiveEmployees}</h4>
                </div>
                <div>
                  <span className="p-2 br-10 bg-danger-transparent border border-danger d-flex align-items-center justify-content-center">
                    <i className="ti ti-user-exclamation text-danger fs-18"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <PreLoader showLoader={isLoading} />
      <DeleteModal
        heading="Confirm Delete"
        message={deleteItem.message}
        onDelete={() => deleteData(deleteItem?.id)}
      />
      <CustomToast
        show={toastSettings?.show}
        message={toastSettings?.message}
        heading={toastSettings?.heading}
        type={toastSettings?.type}
        onClose={() => {
          setToastSettings((prev) => ({
            ...prev,
            show: false,
          }));
        }}
      />
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Employee</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Employee</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Employee List
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
              <div className="mb-2">
                <button
                  disabled={!hasOrgMgntCreate()}
                  data-bs-toggle="modal"
                  data-inert={true}
                  data-bs-target="#add_employee"
                  className="btn btn-primary d-flex align-items-center"
                >
                  <i className="ti ti-circle-plus me-2" />
                  Add Employee
                </button>
              </div>
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
          {/* /Breadcrumb */}
          {renderStatsCards()}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <h5>Employee List</h5>
              <div className="view-toggle">
                <button 
                  className={`btn btn-sm me-2 ${viewType === 'table' ? 'btn-primary' : 'btn-outline-primary'}`} 
                  onClick={() => setViewType('table')}
                  aria-label="Switch to table view"
                >
                  <i className="ti ti-table me-1"></i> Table View
                </button>
                <button 
                  className={`btn btn-sm ${viewType === 'card' ? 'btn-primary' : 'btn-outline-primary'}`} 
                  onClick={() => setViewType('card')}
                  aria-label="Switch to card view"
                >
                  <i className="ti ti-layout-grid me-1"></i> Card View
                </button>
              </div>
            </div>
            <div className="card-body p-0">
              {viewType === 'table' ? (
                <Table
                  dataSource={employees}
                  columns={columns}
                  Selection={false}
                  refresh={fetchEmployees}
                />
              ) : (
                <div className="employee-cards p-3">
                  <div className="row">
                    {employees.map((employee: any) => (
                      <div className="col-xl-4 col-lg-6 col-md-6 mb-3" key={employee.employeeId}>
                        <div className="card h-100 border shadow-sm">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <div className="d-flex align-items-center">
                                {employee.profilePictureUrl ? (
                                  <div className="avatar avatar-xxl me-3 flex-shrink-0">
                                    <img 
                                      src={employee.profilePictureUrl} 
                                      alt={`${employee.firstName} ${employee.lastName}`}
                                      className="avatar-img rounded-circle"
                                    />
                                  </div>
                                ) : (
                                  <div className="avatar avatar-xxl me-3 flex-shrink-0 bg-light-primary rounded-circle">
                                    <span className="text-primary fw-bold">
                                      {(employee.firstName || '').charAt(0).toUpperCase()}
                                    </span>
                                  </div>
                                )}
                                <div>
                                  <h6 className="mb-0 fw-semibold employee-title">
                                    {employee.firstName} {employee.lastName}
                                  </h6>
                                  <small className="text-muted">{employee.username}</small>
                                </div>
                              </div>
                              <span className="badge badge-soft-primary">ID: {employee.employeeId}</span>
                            </div>
                            
                            <div className="employee-info mb-3">
                              <div className="row">
                                <div className="col-12">
                                  <div className="fs-12 fw-medium text-dark">Organization Info</div>
                                  <div className="fs-12 d-flex align-items-center gap-1">
                                    <div className="fw-medium">Designation:</div>
                                    <div>{employee.designationName}</div>
                                  </div>
                                  <div className="fs-12 d-flex align-items-center gap-1">
                                    <div className="fw-medium">Department:</div>
                                    <div>{employee.departmentName}</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            <div className="employee-contact mb-3">
                              <div className="row">
                                <div className="col-12">
                                  <div className="fs-12 d-flex align-items-center gap-1 mb-1">
                                    <i className="ti ti-mail text-muted"></i>
                                    <div className="text-truncate">{employee.email}</div>
                                  </div>
                                  <div className="fs-12 d-flex align-items-center gap-1">
                                    <i className="ti ti-phone text-muted"></i>
                                    <div>{employee.phoneNumber}</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            <div className="d-flex justify-content-end mt-3">
                              <Link
                                hidden={!hasOrgMgntEdit()}
                                to="#"
                                className="btn btn-sm btn-outline-primary me-2"
                                data-bs-toggle="modal" data-inert={true}
                                data-bs-target="#edit_employee"
                                onClick={() => { onEdit(employee) }}
                                title="Edit Employee"
                              >
                                <i className="ti ti-edit me-1" /> Edit
                              </Link>
                              <Link
                                hidden={!hasOrgMgntDelete()}
                                to="#"
                                className="btn btn-sm btn-outline-danger"
                                data-bs-toggle="modal" data-inert={true}
                                data-bs-target="#delete_modal"
                                onClick={() => setDeleteItem({
                                  id: employee.employeeId,
                                  message: `Are you sure you want to delete ${employee.firstName} ${employee.lastName}? This can't be undone.`,
                                })}
                                title="Delete Employee"
                              >
                                <i className="ti ti-trash me-1" /> Delete
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-center border-top bg-white p-3">
          <CopyRightInfo />
        </div>
      </div>
      {/* /Page Wrapper */}
      {/* Add Employee */}
      {renderAddEmployee()}
      {/* /Add Employee */}
      {/* Edit Employee */}
      {renderEditEmployee()}
      {/* /Edit Employee */}
      {renderFacultySchedule()}
    </>
  );
};

export default EmployeeList;